import { useMemo, useState, type ReactElement } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import type { PlatformAdmin } from '@kairos/admin-contract/domains/settings'
import { Download, Plus } from 'lucide-react'

import {
  ArchiveFlagDialog,
  buildToggleFlagPatch,
  FeatureFlagDrawer,
  FilterStrip,
  FlagDialog,
  FlagsTable,
  ToggleFlagDialog,
  buildFeatureFlagDirectories,
  featureFlagsToCsv,
  filterFeatureFlagRows,
  flagAuditQuery,
  flagsQuery,
  toFeatureFlagVisualRow,
  useAddFlagTarget,
  useArchiveFlag,
  useCreateFlag,
  useDeleteFlagTarget,
  useUpdateFlag,
  type FeatureFlagRow,
  type FeatureFlagVisualRow,
  type FilterStripValue,
} from '~/features/feature-flags'
import { meQuery } from '~/features/me'
import {
  organizationsListQuery,
  type OrganizationListRow,
} from '~/features/organizations'
import { platformAdminsQuery } from '~/features/settings'
import { teamsListQuery, type TeamRow } from '~/features/teams'
import { usersListQuery, type UserRow } from '~/features/users'
import { logger } from '~/lib/logger'

export const Route = createFileRoute('/admin/feature-flags')({
  component: FeatureFlagsPage,
})

const DEFAULT_FILTERS: FilterStripValue = {
  search: '',
  view: 'active',
  owner: '',
}
const EMPTY_FLAGS: readonly FeatureFlagRow[] = []
const EMPTY_ADMINS: readonly PlatformAdmin[] = []
const EMPTY_USERS: readonly UserRow[] = []
const EMPTY_TEAMS: readonly TeamRow[] = []
const EMPTY_ORGANIZATIONS: readonly OrganizationListRow[] = []

function FeatureFlagsPage(): ReactElement {
  const [filters, setFilters] =
    useState<FilterStripValue>(DEFAULT_FILTERS)
  const [createOpen, setCreateOpen] = useState(false)
  const [editKey, setEditKey] = useState<string | null>(null)
  const [toggleKey, setToggleKey] = useState<string | null>(null)
  const [archiveKey, setArchiveKey] = useState<string | null>(null)

  const createMutation = useCreateFlag()
  const updateMutation = useUpdateFlag()
  const archiveMutation = useArchiveFlag()
  const addTargetMutation = useAddFlagTarget()
  const deleteTargetMutation = useDeleteFlagTarget()
  const flagsList = useQuery(flagsQuery({}))
  const adminsQuery = useQuery(platformAdminsQuery())
  const usersDirectoryQuery = useQuery(
    usersListQuery({ limit: 200, offset: 0 }),
  )
  const teamsDirectoryQuery = useQuery(
    teamsListQuery({ limit: 200, offset: 0, archived: 'active' }),
  )
  const organizationsDirectoryQuery = useQuery(
    organizationsListQuery({ limit: 200, offset: 0 }),
  )
  const currentAdmin = useQuery(meQuery())
  const auditQuery = useQuery(flagAuditQuery(editKey ?? ''))

  const rows = flagsList.data?.flags ?? EMPTY_FLAGS
  const admins = adminsQuery.data?.admins ?? EMPTY_ADMINS
  const directories = useMemo(
    () =>
      buildFeatureFlagDirectories({
        admins,
        users: usersDirectoryQuery.data?.users ?? EMPTY_USERS,
        teams: teamsDirectoryQuery.data?.teams ?? EMPTY_TEAMS,
        organizations:
          organizationsDirectoryQuery.data?.organizations ??
          EMPTY_ORGANIZATIONS,
      }),
    [
      admins,
      organizationsDirectoryQuery.data?.organizations,
      teamsDirectoryQuery.data?.teams,
      usersDirectoryQuery.data?.users,
    ],
  )
  const ownerNames = useMemo(
    () =>
      new Map(
        directories.owners.map((owner) => [owner.id, owner.label]),
      ),
    [directories.owners],
  )
  const rowsByKey = useMemo(
    () => new Map(rows.map((row) => [row.key, row])),
    [rows],
  )
  const filteredRows = useMemo(
    () =>
      filterFeatureFlagRows(rows, {
        ...filters,
        currentUserId: currentAdmin.data?.user_id ?? '',
      }),
    [currentAdmin.data?.user_id, filters, rows],
  )
  const visualRows = useMemo<readonly FeatureFlagVisualRow[]>(
    () =>
      filteredRows.map((row) =>
        toFeatureFlagVisualRow(row, ownerNames),
      ),
    [filteredRows, ownerNames],
  )

  const editTarget = editKey ? rowsByKey.get(editKey) ?? null : null
  const toggleTarget = toggleKey
    ? rowsByKey.get(toggleKey) ?? null
    : null
  const archiveTarget = archiveKey
    ? rowsByKey.get(archiveKey) ?? null
    : null
  const mutationError =
    createMutation.error ??
    updateMutation.error ??
    archiveMutation.error ??
    addTargetMutation.error ??
    deleteTargetMutation.error

  const findRow = (visual: FeatureFlagVisualRow): FeatureFlagRow | null =>
    rowsByKey.get(visual.key) ?? null

  const openEdit = (visual: FeatureFlagVisualRow): void => {
    const row = findRow(visual)
    if (row) setEditKey(row.key)
  }
  const openToggle = (visual: FeatureFlagVisualRow): void => {
    const row = findRow(visual)
    if (row) setToggleKey(row.key)
  }
  const openArchive = (visual: FeatureFlagVisualRow): void => {
    const row = findRow(visual)
    if (row) setArchiveKey(row.key)
  }

  const exportRows = (): void => {
    const csv = featureFlagsToCsv(filteredRows, ownerNames)
    const url = URL.createObjectURL(
      new Blob([csv], { type: 'text/csv;charset=utf-8' }),
    )
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `feature-flags-${new Date().toISOString().slice(0, 10)}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ka-page ka-page--flags">
      <FilterStrip
        value={filters}
        onChange={setFilters}
        owners={directories.owners}
      />

      <div className="ka-flags-actions">
        <button
          type="button"
          className="ka-btn ka-btn--secondary ka-btn--sm"
          onClick={exportRows}
          disabled={filteredRows.length === 0}
        >
          <Download size={12} aria-hidden="true" /> Export visible
        </button>
        <button
          type="button"
          className="ka-btn ka-btn--primary"
          onClick={() => setCreateOpen(true)}
        >
          <Plus size={14} aria-hidden="true" /> New Flag
        </button>
      </div>

      {adminsQuery.isError || usersDirectoryQuery.isError ? (
        <div role="alert" className="ka-inline-error">
          Owners could not be loaded. Editing remains unavailable until the
          platform-admin list can be read.
        </div>
      ) : null}
      {mutationError ? (
        <div role="alert" className="ka-inline-error">
          {mutationError instanceof Error
            ? mutationError.message
            : 'The feature flag change could not be saved.'}
        </div>
      ) : null}

      <FlagsTable
        flags={visualRows}
        isLoading={flagsList.isLoading}
        error={flagsList.error instanceof Error ? flagsList.error : null}
        onRowClick={openEdit}
        onToggle={openToggle}
        onArchive={openArchive}
        toggleInFlightKey={
          updateMutation.isPending ? toggleTarget?.key ?? null : null
        }
        hasFilters={
          filters.search.length > 0 ||
          filters.owner.length > 0 ||
          filters.view !== 'all'
        }
        onClearFilters={() =>
          setFilters({ search: '', view: 'all', owner: '' })
        }
        onCreateFlag={() => setCreateOpen(true)}
      />

      {createOpen ? (
        <FlagDialog
          mode="create"
          isSubmitting={createMutation.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={(payload) => {
            if (payload.mode !== 'create') return
            createMutation.mutate(payload.values, {
              onSuccess: () => setCreateOpen(false),
              onError: (cause) =>
                logger.warn('feature-flag create failed', { cause }),
            })
          }}
        />
      ) : null}

      {editTarget ? (
        <FeatureFlagDrawer
          key={editTarget.key}
          flag={editTarget}
          directories={directories}
          auditEntries={auditQuery.data?.entries ?? []}
          auditLoading={auditQuery.isLoading}
          auditError={auditQuery.error}
          currentAdminId={currentAdmin.data?.user_id ?? ''}
          isSaving={updateMutation.isPending}
          isTargetMutating={
            addTargetMutation.isPending || deleteTargetMutation.isPending
          }
          saveError={updateMutation.error}
          targetError={
            addTargetMutation.error ?? deleteTargetMutation.error
          }
          onClose={() => setEditKey(null)}
          onSave={(patch) => {
            updateMutation.mutate(
              { key: editTarget.key, patch },
              { onSuccess: () => setEditKey(null) },
            )
          }}
          onAddTarget={(target) => {
            addTargetMutation.mutate({
              key: editTarget.key,
              target,
            })
          }}
          onDeleteTarget={(targetId) => {
            deleteTargetMutation.mutate({
              key: editTarget.key,
              targetId,
            })
          }}
        />
      ) : null}

      {toggleTarget ? (
        <ToggleFlagDialog
          flag={toggleTarget}
          isSubmitting={updateMutation.isPending}
          error={updateMutation.error}
          onClose={() => setToggleKey(null)}
          onConfirm={() => {
            updateMutation.mutate(
              {
                key: toggleTarget.key,
                patch: buildToggleFlagPatch(toggleTarget),
              },
              { onSuccess: () => setToggleKey(null) },
            )
          }}
        />
      ) : null}

      {archiveTarget ? (
        <ArchiveFlagDialog
          flag={archiveTarget}
          isSubmitting={archiveMutation.isPending}
          onClose={() => setArchiveKey(null)}
          onConfirm={(reason) => {
            archiveMutation.mutate(
              { key: archiveTarget.key, reason },
              { onSuccess: () => setArchiveKey(null) },
            )
          }}
        />
      ) : null}
    </div>
  )
}
